import Vue from 'vue'
import gql from 'graphql-tag'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { CarrierProduct } from '@/modules/CarrierProduct/interfaces/carrier-product'
import { ApolloResponse } from '@/interfaces/apollo'
import { SnackType } from '@/interfaces/snack'
import { showErrors, showNotification } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import carrierProductDialog from '@/modules/Carrier/scripts/carrier-dialog'
import { getCarrierProducts, editCarrierProduct } from '@/modules/CarrierProduct/gql/carrier-product.gql'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField
  }
})
export default class CarrierProductList extends mixins(carrierProductDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Setting'
    }
  ]
  searchVal = ''

  carrierProducts: CarrierProduct[] = []

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    name: 'NAME'
  }

  renaming = false
  nullCarrierProductName = false
  renameCarrierProductName = ''

  tableColumn = [
    { field: 'name',
      title: 'Insurance Product Name',
      slots: { default: 'name_default' },
      resizable: true,
      sortable: true
    },
    { title: 'NAIC Code',
      field: 'naicCode',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.naicCode ? row.naicCode : '-')
        ]
      }
      },
      resizable: true,
      sortable: true
    },
    { title: 'New Business Commission',
      field: 'newBusinessCommission',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.newBusinessCommission ? row.newBusinessCommission : '-')
        ]
      }
      },
      resizable: true,
      sortable: true
    },
    { title: 'Renewal Commission',
      field: 'renewalCommission',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.renewalCommission ? row.renewalCommission : '-')
        ]
      }
      },
      resizable: true,
      sortable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 120 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'id',
    sortDesc: true
  }

  mounted () {
    this.initHandler()
  }

  @Watch('$route.name')
  onRouteChanged (val: string, oldVal: string) {
    if (!oldVal) {
      return
    }
    this.initHandler()
  }

  initHandler () {
    this.breadcrumbs = [
      {
        text: 'Setting'
      }
    ]
    if (this.$route.params.carrierId) {
      this.breadcrumbs.push({
        text: 'Carriers',
        to: {
          name: 'CarrierList'
        }
      })
      this.breadcrumbs.push({
        text: this.$route.params.carrierName,
        to: {
          name: 'CarrierDetailRelated',
          params: {
            id: this.$route.params.carrierId
          }
        }
      })
    }
    this.breadcrumbs.push({
      text: 'Insurance Products'
    })
    this.getCarrierProducts()
  }

  async getCarrierProducts () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'ID'
    const asc = sortDesc || false
    const carrierId = Number(this.$route.params.carrierId)
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCarrierProducts,
        variables: {
          filter: {
            name: searchVal,
            carrierId
          },
          order: {
            orderBy: orderBy,
            asc: !asc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.carrierProducts = result.data.getCarrierProducts.carrierProducts
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getCarrierProducts.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  renameCarrierProduct (e: Event, item: any) {
    e.preventDefault()
    e.stopPropagation()
    this.targetCarrierProduct = Object.assign({}, item)
    this.renameCarrierProductName = item.name
    this.renaming = true
  }

  stopPreventDefault (e: Event) {
    e.preventDefault()
    e.stopPropagation()
  }

  async renameConfirm (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.nullCarrierProductName = !this.renameCarrierProductName
    if (this.nullCarrierProductName) {
      return
    }
    await this.renameCarrierProductNameConfirm()
    this.renaming = false
  }

  async renameCancel (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.renaming = false
    this.renameCarrierProductName = ''
  }

  async renameCarrierProductNameConfirm () {
    if (!this.targetCarrierProduct) {
      return
    }
    const { id } = this.targetCarrierProduct
    try {
      await this.$apollo.mutate({
        mutation: editCarrierProduct,
        variables: {
          carrierProductId: Number(id),
          carrierProductInput: {
            name: this.renameCarrierProductName
          }
        }
      })
      showNotification({
        msg: 'Update the carrierProduct name successfully',
        type: SnackType.Success
      })
      this.getCarrierProducts()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  refreshList () {
    this.searchVal = ''
    this.getCarrierProducts()
  }

  async deleteCarrierProductConfirm () {
    await this.deleteCarrierProductPost()
    this.getCarrierProducts()
  }

  routeToDetail (item: any) {
    return {
      name: 'CarrierProductDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToCarrierDetail (item: any) {
    if (item.carrier) {
      this.$router.push({
        name: 'CarrierDetailDetail',
        params: {
          id: item.carrier.id
        }
      })
    }
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getCarrierProducts()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getCarrierProducts()
  }
}
